export const demoIds = [
  'portfolio-operations',
  'routine-wellness',
  'inventory-sales',
  'commerce-brand',
  'cross-company-reporting',
] as const;

export type DemoId = (typeof demoIds)[number];

export type DemoDefinition = {
  id: DemoId;
  title: string;
  shortTitle: string;
  description: string;
  steps: readonly string[];
  posterAlt: string;
};

export const demoDefinitions: Record<DemoId, DemoDefinition> = {
  'portfolio-operations': {
    id: 'portfolio-operations',
    title: 'Portfolio Operations Platform',
    shortTitle: 'Portfolio operations',
    description: 'Select an entity, review connected properties, and ask a read-only assistant for an operational summary.',
    steps: ['Portfolio', 'Entity', 'Properties', 'Assistant', 'Summary'],
    posterAlt: 'Fictional portfolio operations interface showing connected business properties and a read-only assistant summary.',
  },
  'routine-wellness': {
    id: 'routine-wellness',
    title: 'Routine & Wellness Companion App',
    shortTitle: 'Routine and wellness',
    description: 'Review today’s schedule, open a reminder, log completion, and see what comes next.',
    steps: ['Today', 'Reminder', 'Log', 'Complete', 'Next'],
    posterAlt: 'Fictional mobile routine companion showing today’s schedule, a completed check-in, and the next reminder.',
  },
  'inventory-sales': {
    id: 'inventory-sales',
    title: 'Inventory & Sales Operations',
    shortTitle: 'Inventory and sales',
    description: 'Import a recurring export, validate the rows, identify an inventory issue, and open the action view.',
    steps: ['Source', 'Import', 'Validate', 'Review', 'Action'],
    posterAlt: 'Fictional inventory operations interface showing a validated import and a low-stock action queue.',
  },
  'commerce-brand': {
    id: 'commerce-brand',
    title: 'E-Commerce & Brand Operations',
    shortTitle: 'Commerce and brand',
    description: 'Validate an inventory feed, approve a content update, and show the connected customer site updating.',
    steps: ['Feed', 'Validate', 'Approve', 'Publish', 'Live'],
    posterAlt: 'Fictional commerce operations interface showing a validated feed and a connected customer website update.',
  },
  'cross-company-reporting': {
    id: 'cross-company-reporting',
    title: 'Cross-Company Reporting',
    shortTitle: 'Cross-company reporting',
    description: 'Switch entity scope, compare aggregate reporting, and demonstrate permission boundaries.',
    steps: ['Portfolio', 'Switch', 'Compare', 'Access', 'Ready'],
    posterAlt: 'Fictional cross-company reporting interface showing aggregate results and permission-aware access.',
  },
};

export function isDemoId(value: string | null): value is DemoId {
  return demoIds.includes(value as DemoId);
}
